import React, { useState } from "react"
import {
  Drawer,
  Typography,
  Space,
  Button,
  Divider,
  message,
  Form,
  DatePicker,
  Input,
  Select,
  Card,
  Tag,
  Row,
  Col,
  Descriptions,
} from "antd"
import {
  doc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  Timestamp as FSTimestamp,
} from "firebase/firestore"
import { db } from "../../../../firebase/config"
import dayjs from "dayjs"

const { Text } = Typography
const { TextArea } = Input

function parseTs(ts) {
  if (!ts) return null
  if (typeof ts.toDate === "function") return ts.toDate()
  if (ts.seconds != null && ts.nanoseconds != null) {
    return new Date(ts.seconds * 1000 + ts.nanoseconds / 1e6)
  }
  if (ts instanceof Date) return ts
  return null
}

const CONTACT_TYPES = [
  { value: 0, label: "Email" },
  { value: 1, label: "Phone" },
  { value: 2, label: "In Person" },
]

export default function ContactStatusDrawer({ text = "Open", record }) {
  const [open, setOpen] = useState(false)
  const [extendSubmitting, setExtendSubmitting] = useState(false)
  const [contactSubmitting, setContactSubmitting] = useState(false)

  const [extendForm] = Form.useForm()
  const [contactForm] = Form.useForm()

  const current = (() => {
    const d = parseTs(record.payUntil)
    return d ? dayjs(d) : null
  })()
  const today = dayjs().startOf("day")
  const maxExtend = today.add(14, "day")
  const canExtend = !record.originalPayUntil

  const showDrawer = () => {
    extendForm.setFieldsValue({ extendPayUntil: current })
    contactForm.setFieldsValue({
      contactDate: dayjs(),
      contactType: undefined,
      contactResponse: "",
    })
    setOpen(true)
  }
  const onClose = () => setOpen(false)

  const contactLabel = (v) => CONTACT_TYPES.find((t) => t.value === v)?.label || "Other"

  const renderHistory = () => {
    if (!Array.isArray(record.unpaidContactInfo) || !record.unpaidContactInfo.length) {
      return <Text type="secondary">No contact has been made yet.</Text>
    }
    return record.unpaidContactInfo
      .sort((a, b) => (b.created?.seconds || 0) - (a.created?.seconds || 0))
      .map((c, idx) => (
        <Card
          key={idx}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <Tag color="blue">{contactLabel(c.contactType)}</Tag>
              {dayjs(c.contactDate?.seconds ? c.contactDate.seconds * 1000 : c.contactDate).format(
                "DD.MM.YYYY"
              )}
            </Space>
          }>
          <Text>{c.contactResponse}</Text>
        </Card>
      ))
  }

  const onExtendFinish = async (values) => {
    setExtendSubmitting(true)
    try {
      if (values.extendPayUntil && canExtend && !values.extendPayUntil.isSame(current, "day")) {
        const newTs = FSTimestamp.fromDate(values.extendPayUntil.toDate())
        const invRef = doc(db, "invoices", record.id)
        const updates = {
          payUntil: newTs,
          payUntilTimestamp: newTs,
        }
        if (!record.originalPayUntil) {
          updates.originalPayUntil = record.payUntil
          updates.originalPayUntilTimestamp = record.payUntilTimestamp
        }
        await updateDoc(invRef, updates)
        message.success("Due date extended!")
        extendForm.resetFields()
      }
    } catch (err) {
      console.error(err)
      message.error("Could not extend due date.")
    } finally {
      setExtendSubmitting(false)
    }
  }

  const onContactFinish = async (values) => {
    setContactSubmitting(true)
    try {
      const payload = {
        contactDate: values.contactDate.toDate(),
        contactType: values.contactType,
        contactResponse: values.contactResponse,
        created: new Date(),
      }
      const invRef = doc(db, "invoices", record.id)
      await updateDoc(invRef, {
        unpaidContactInfo: arrayUnion(payload),
        hasContactStatus: true,
        lastContactLogged: serverTimestamp(),
      })
      message.success("Contact status saved!")
      contactForm.resetFields()
      onClose()
    } catch (err) {
      console.error(err)
      message.error("Could not save contact info.")
    } finally {
      setContactSubmitting(false)
    }
  }

  const trigger = (
    <Space split={<Divider type="vertical" />}>
      <Text type="primary" onClick={showDrawer}>
        {record.hasContactStatus ? "Contacted" : text}
      </Text>
      <Button onClick={showDrawer} size="small" type="primary">
        Update
      </Button>
    </Space>
  )

  return (
    <div>
      {trigger}
      <Drawer
        title={`Contact Status • ${record.docPrefix}${String(record.docNumber).padStart(4, "0")}`}
        width={1000}
        onClose={onClose}
        open={open}>
        {renderHistory()}
        <Divider />

        {/* Contact Status Form */}
        <Row gutter={[50, 15]}>
          <Col span={12}>
            <Form
              form={contactForm}
              layout="vertical"
              onFinish={onContactFinish}
              name="contactStatus"
              initialValues={{ contactDate: dayjs() }}>
              <Form.Item
                label="Contact Date"
                name="contactDate"
                rules={[{ required: true, message: "Select the contact date" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Contact Type"
                name="contactType"
                rules={[{ required: true, message: "Select the contact type" }]}>
                <Select options={CONTACT_TYPES} />
              </Form.Item>

              <Form.Item
                label="Response / Notes"
                name="contactResponse"
                rules={[{ required: true, message: "Please enter a note" }]}>
                <TextArea rows={4} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block loading={contactSubmitting}>
                  Save Contact Status
                </Button>
              </Form.Item>
            </Form>
          </Col>
          <Col span={12}>
            <Descriptions column={1} title="Client Contact Information" layout="vertical">
              <Descriptions.Item label="Contact Details">
                <Input size="small" value={record?.client?.contactName} readOnly />
              </Descriptions.Item>
              <Descriptions.Item label="Contact Phone">
                <Input size="small" value={record?.client?.contactNumber} readOnly />
              </Descriptions.Item>
              <Descriptions.Item label="Contact Email">
                <Input size="small" value={record?.client?.contactEmail} readOnly />
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        <Divider />

        {/* Extend Due Date Form */}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form form={extendForm} layout="vertical" onFinish={onExtendFinish} name="extendDue">
              <Form.Item
                label="Extend Due Date"
                name="extendPayUntil"
                rules={[
                  () => ({
                    validator(_, val) {
                      if (
                        !val ||
                        (canExtend &&
                          (val.isSame(today, "day") ||
                            (val.isAfter(today, "day") && val.isBefore(maxExtend, "day"))))
                      ) {
                        return Promise.resolve()
                      }
                      return Promise.reject(
                        new Error("Select a date from today up to 14 days ahead")
                      )
                    },
                  }),
                ]}>
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(d) => d.isBefore(today, "day") || d.isAfter(maxExtend, "day")}
                  disabled={!canExtend}
                  placeholder={canExtend ? undefined : "Already extended"}
                />
                {!canExtend && (
                  <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                    You've already extended this invoice's due date.
                  </Text>
                )}
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={extendSubmitting} block>
                  Extend Due Date
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Drawer>
    </div>
  )
}
